import { memo, useCallback, useMemo, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  BaseEdge,
  EdgeLabelRenderer,
  type Edge,
  type EdgeProps,
  Handle,
  Position,
  useViewport,
  type Node,
  type NodeTypes,
  type EdgeTypes,
  type BackgroundVariant
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'

interface ControlPoint {
  id: string
  x: number
  y: number
}

interface StepEdgeData extends Record<string, unknown> {
  // Store the middle vertical X position
  midX?: number
  // Whether the middle vertical ghost point has been activated
  midActive?: boolean
  // Active control points along the edge.
  // Для варианта A мы используем их так:
  // - left-N: ступени возле левой ноды (индекс N задаёт порядок ступеней)
  // - right-N: ступени возле правой ноды
  // X мы храним как индекс (N), а фактический X считаем по геометрии,
  // поэтому для построения пути используется только Y.
  controlPoints?: ControlPoint[]
}

interface DemoNodeData extends Record<string, unknown> {
  label: string
}

const DemoNode = memo(function DemoNode({ data }: { data: DemoNodeData }) {
  return (
    <div className="w-14 h-14 bg-white border border-gray-300 rounded-xl shadow-sm flex items-center justify-center text-gray-600 font-semibold relative">
      {data.label}
      <Handle
        type="source"
        position={Position.Right}
        id="out"
        style={{
          background: '#ffffff',
          border: '2px solid #a3a3a3',
          width: 12,
          height: 12,
          borderRadius: '50%',
          marginRight: -6
        }}
      />
      <Handle
        type="target"
        position={Position.Left}
        id="in"
        style={{
          background: '#ffffff',
          border: '2px solid #a3a3a3',
          width: 12,
          height: 12,
          borderRadius: '50%',
          marginLeft: -6
        }}
      />
    </div>
  )
})

const nodeTypes: NodeTypes = {
  demo: DemoNode
}

const initialNodes: Node<DemoNodeData>[] = [
  {
    id: 'step-left',
    type: 'demo',
    // Чуть ниже правого блока — примерно на 5 высот ноды
    position: { x: 120, y: 600 },
    data: { label: '3A' }
  },
  {
    id: 'step-right',
    type: 'demo',
    position: { x: 520, y: 320 },
    data: { label: '3B' }
  }
]

const initialEdges: Edge<StepEdgeData>[] = [
  {
    id: 'edge-step',
    source: 'step-left',
    target: 'step-right',
    sourceHandle: 'out',
    targetHandle: 'in',
    type: 'step',
    data: {},
    style: { stroke: '#fb923c', strokeWidth: 4 }
  }
]

function GhostPoint({
  x,
  y,
  axis = 'x',
  variant = 'ghost',
  onDrag,
  onDragEnd
}: {
  x: number
  y: number
  axis?: 'x' | 'y'
  variant?: 'ghost' | 'active'
  onDrag?: (pos: { x: number; y: number }) => void
  onDragEnd?: (pos: { x: number; y: number }) => void
}) {
  const { zoom } = useViewport()
  const isDragging = useRef(false)

  const handlePointerDown = useCallback(
    (event: React.PointerEvent) => {
      event.stopPropagation()
      event.preventDefault()
      isDragging.current = true
      const startX = event.clientX
      const startY = event.clientY
      const initialX = x
      const initialY = y

      const handleMove = (e: PointerEvent) => {
        if (!isDragging.current) return
        const deltaX = (e.clientX - startX) / zoom
        const deltaY = (e.clientY - startY) / zoom

        const newX = axis === 'x' ? initialX + deltaX : initialX
        const newY = axis === 'y' ? initialY + deltaY : initialY

        if (onDrag) {
          onDrag({ x: newX, y: newY })
        }
      }

      const handleUp = (e: PointerEvent) => {
        if (!isDragging.current) return
        isDragging.current = false
        window.removeEventListener('pointermove', handleMove)
        window.removeEventListener('pointerup', handleUp)

        if (onDragEnd) {
          const deltaX = (e.clientX - startX) / zoom
          const deltaY = (e.clientY - startY) / zoom
          const finalX = axis === 'x' ? initialX + deltaX : initialX
          const finalY = axis === 'y' ? initialY + deltaY : initialY
          onDragEnd({ x: finalX, y: finalY })
        }
      }

      window.addEventListener('pointermove', handleMove)
      window.addEventListener('pointerup', handleUp)
    },
    [axis, onDrag, x, y, zoom]
  )

  return (
    <div
      onPointerDown={handlePointerDown}
      style={{
        position: 'absolute',
        transform: `translate(-50%, -50%) translate(${x}px, ${y}px)`,
        width: 14,
        height: 14,
        borderRadius: '50%',
        border: '2px solid #fb923c',
        background: variant === 'active' ? '#fb923c' : 'rgba(255,255,255,0.9)',
        cursor: 'grab',
        pointerEvents: 'auto',
        zIndex: 1000
      }}
    />
  )
}

const StepEdge = memo(function StepEdge(props: EdgeProps) {
  const { id, sourceX, sourceY, targetX, targetY, selected } = props
  const data = (props.data || {}) as StepEdgeData

  const offset = 30
  const startX = sourceX + offset
  const endX = targetX - offset
  // базовый X центральной вертикали зависит только от изначальных невидимых точек
  const baseMidX = (startX + endX) / 2
  const midX = data.midX ?? baseMidX
  const midActive = data.midActive ?? false

  const controlPoints = data.controlPoints ?? []

  // Ступени возле левой и правой нод:
  // - leftSteps: массив точек с id вида left-1, left-2, ... (сортируем по индексу)
  // - rightSteps: аналогично для правой ноды
  const leftSteps = useMemo(
    () =>
      controlPoints
        .filter((p) => p.id.startsWith('left-'))
        .sort((a, b) => {
          const aIdx = parseInt(a.id.replace('left-', ''), 10) || 0
          const bIdx = parseInt(b.id.replace('left-', ''), 10) || 0
          return aIdx - bIdx
        }),
    [controlPoints]
  )

  const rightSteps = useMemo(
    () =>
      controlPoints
        .filter((p) => p.id.startsWith('right-'))
        .sort((a, b) => {
          const aIdx = parseInt(a.id.replace('right-', ''), 10) || 0
          const bIdx = parseInt(b.id.replace('right-', ''), 10) || 0
          return aIdx - bIdx
        }),
    [controlPoints]
  )

  // Y-координата конца "лестницы" слева: либо последняя ступень, либо уровень source
  const leftEndY = leftSteps.length > 0 ? leftSteps[leftSteps.length - 1].y : sourceY

  // Y-координата конца "лестницы" справа: либо последняя ступень, либо уровень target
  const rightEndY = rightSteps.length > 0 ? rightSteps[rightSteps.length - 1].y : targetY

  // Геометрия для распределения ступеней слева/справа — используем и для пути, и для ghost-точек
  const leftTotalSegments = leftSteps.length + 1
  const leftStepGap = (midX - startX) / leftTotalSegments

  const rightTotalSegments = rightSteps.length + 1
  const rightStepGap = (endX - midX) / rightTotalSegments

  const path = useMemo(() => {
    const commands: string[] = []

    // Начинаем с source
    let currentX = sourceX
    let currentY = sourceY
    commands.push(`M ${sourceX} ${sourceY}`)

    // --- Лестница слева от midX ---
    if (leftSteps.length === 0) {
      // Без ступеней: просто горизонталь к midX
      commands.push(`L ${midX} ${sourceY}`)
      currentX = midX
      currentY = sourceY
    } else {
      // Несколько ступеней: распределяем их по X между source и midX
      leftSteps.forEach((step, index) => {
        const verticalX = startX + leftStepGap * (index + 1)
        // горизонталь от текущей точки до вертикали
        commands.push(`L ${verticalX} ${currentY}`)
        // вертикаль до уровня ступени
        commands.push(`L ${verticalX} ${step.y}`)
        currentX = verticalX
        currentY = step.y
      })

      // После последней ступени идём горизонтально к midX на уровне currentY
      commands.push(`L ${midX} ${currentY}`)
      currentX = midX
      currentY = leftEndY
    }

    // --- Центральный вертикальный сегмент midX: от leftEndY к rightEndY ---
    if (leftEndY !== rightEndY) {
      commands.push(`L ${midX} ${rightEndY}`)
      currentX = midX
      currentY = rightEndY
    }

    // --- Лестница справа от midX до target ---
    if (rightSteps.length === 0) {
      // Без ступеней: горизонталь к targetX, затем вертикаль к targetY
      if (currentX !== targetX || currentY !== targetY) {
        commands.push(`L ${targetX} ${currentY}`)
        commands.push(`L ${targetX} ${targetY}`)
      }
    } else {
      // Ступени задаются от ноды, но строим путь от midX к target:
      // идём от midX к последней ступени, затем к предпоследней и т.д.
      const stepsFromMid = [...rightSteps].reverse()
      stepsFromMid.forEach((step, index) => {
        const verticalX = endX - rightStepGap * (index + 1)
        // горизонталь от текущей точки до вертикали
        commands.push(`L ${verticalX} ${currentY}`)
        // вертикаль до уровня ступени
        commands.push(`L ${verticalX} ${step.y}`)
        currentX = verticalX
        currentY = step.y
      })

      // После последней ступени идём к targetX и опускаемся/поднимаемся до targetY
      if (currentX !== targetX || currentY !== targetY) {
        commands.push(`L ${targetX} ${currentY}`)
        commands.push(`L ${targetX} ${targetY}`)
      }
    }

    return commands.join(' ')
  }, [leftSteps, rightSteps, leftEndY, rightEndY, midX, sourceX, sourceY, targetX, targetY, startX, endX])

  // центральная ghost-точка на вертикальном сегменте midX: посередине между концами лестниц
  const ghostY = (leftEndY + rightEndY) / 2
  // "невидимые" точки возле нод фиксированы на startX / endX,
  // а ghost-точки должны появляться между ними и ближайшей вертикалью.
  const leftNearestVerticalX = leftSteps.length === 0 ? midX : startX + leftStepGap
  const rightNearestVerticalX = rightSteps.length === 0 ? midX : endX - rightStepGap
  const leftNodeGhostX = (startX + leftNearestVerticalX) / 2
  const rightNodeGhostX = (endX + rightNearestVerticalX) / 2

  return (
    <>
      <BaseEdge path={path} {...props} />
      <EdgeLabelRenderer>
        {selected && (
          <>
            {/* Центральная ghost-точка на вертикальном сегменте */}
            <GhostPoint
              x={midX}
              y={ghostY}
              axis="x"
              variant={midActive ? 'active' : 'ghost'}
              onDrag={({ x }) => {
                const event = new CustomEvent('updateStepEdge', {
                  detail: { edgeId: id, midX: x }
                })
                window.dispatchEvent(event)
              }}
            />
            {/* Левая ghost-точка У НОДЫ: создаёт новые ступени слева, двигается только по Y */}
            <GhostPoint
              x={leftNodeGhostX}
              y={sourceY}
              axis="y"
              onDragEnd={({ y }) => {
                const event = new CustomEvent('addLeftStep', {
                  detail: { edgeId: id, y }
                })
                window.dispatchEvent(event)
              }}
            />
            {/* Правая ghost-точка У НОДЫ: создаёт новые ступени справа, двигается только по Y */}
            <GhostPoint
              x={rightNodeGhostX}
              y={targetY}
              axis="y"
              onDragEnd={({ y }) => {
                const event = new CustomEvent('addRightStep', {
                  detail: { edgeId: id, y }
                })
                window.dispatchEvent(event)
              }}
            />
          </>
        )}
      </EdgeLabelRenderer>
    </>
  )
})

const edgeTypes: EdgeTypes = {
  step: StepEdge
}

export default function EditableEdgesDemo() {
  const [nodes, , onNodesChange] = useNodesState<Node<DemoNodeData>>(initialNodes)
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge<StepEdgeData>>(initialEdges)

  // Listen for ghost point updates
  useEffect(() => {
    const handleUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<{ edgeId: string; midX: number }>
      const { edgeId, midX } = customEvent.detail

      console.log('Updating edge:', edgeId, 'new midX:', midX)

      setEdges((current) =>
        current.map((edge) =>
          edge.id === edgeId
            ? {
                ...edge,
                data: {
                  ...(edge.data as StepEdgeData),
                  midX,
                  midActive: true
                }
              }
            : edge
        )
      )
    }

    window.addEventListener('updateStepEdge', handleUpdate)
    return () => window.removeEventListener('updateStepEdge', handleUpdate)
  }, [setEdges])

  // Listen for left control point activation
  // Listen for left steps creation (лестница возле левой ноды)
  useEffect(() => {
    const handleAddStep = (e: Event) => {
      const customEvent = e as CustomEvent<{ edgeId: string; y: number }>
      const { edgeId, y } = customEvent.detail

      console.log('Add left step for edge:', edgeId, 'at Y:', y)

      setEdges((current) =>
        current.map((edge) =>
          edge.id === edgeId
            ? (() => {
                const prevData = (edge.data || {}) as StepEdgeData
                const prevPoints = prevData.controlPoints ?? []
                const existingLeft = prevPoints.filter((p) => p.id.startsWith('left-'))
                const nextIndex = existingLeft.length + 1

                return {
                  ...edge,
                  data: {
                    ...prevData,
                    controlPoints: [
                      ...prevPoints,
                      {
                        id: `left-${nextIndex}`,
                        x: nextIndex,
                        y
                      }
                    ]
                  }
                }
              })()
            : edge
        )
      )
    }

    window.addEventListener('addLeftStep', handleAddStep)
    return () => window.removeEventListener('addLeftStep', handleAddStep)
  }, [setEdges])

  // Listen for right steps creation (лестница возле правой ноды)
  useEffect(() => {
    const handleAddStep = (e: Event) => {
      const customEvent = e as CustomEvent<{ edgeId: string; y: number }>
      const { edgeId, y } = customEvent.detail

      console.log('Add right step for edge:', edgeId, 'at Y:', y)

      setEdges((current) =>
        current.map((edge) =>
          edge.id === edgeId
            ? (() => {
                const prevData = (edge.data || {}) as StepEdgeData
                const prevPoints = prevData.controlPoints ?? []
                const existingRight = prevPoints.filter((p) => p.id.startsWith('right-'))
                const nextIndex = existingRight.length + 1

                return {
                  ...edge,
                  data: {
                    ...prevData,
                    controlPoints: [
                      ...prevPoints,
                      {
                        id: `right-${nextIndex}`,
                        x: nextIndex,
                        y
                      }
                    ]
                  }
                }
              })()
            : edge
        )
      )
    }

    window.addEventListener('addRightStep', handleAddStep)
    return () => window.removeEventListener('addRightStep', handleAddStep)
  }, [setEdges])

  return (
    <div className="h-screen w-full flex flex-col bg-gray-50">
      <div className="border-b bg-white">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Step Edge Test</h1>
            <p className="text-sm text-gray-500">
              Click the edge and drag the ghost point to move the vertical line
            </p>
          </div>
          <Link to="/" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            ← Back to dashboard
          </Link>
        </div>
      </div>

      <div className="flex-1 flex">
        <div className="flex-1 relative">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            fitViewOptions={{ padding: 0.2 }}
            minZoom={0.5}
            maxZoom={1.5}
            className="bg-gray-50"
          >
            <Controls />
            <Background variant={'dots' as BackgroundVariant} gap={24} size={1} color="#d1d5db" />
          </ReactFlow>
        </div>
      </div>
    </div>
  )
}
